import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, rectSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import * as ImageManipulator from 'expo-image-manipulator';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Cropper from 'react-easy-crop';
import { Control, FieldValues, Path, useController } from 'react-hook-form';
import { FlatList, Image, Modal, Platform, Pressable, StyleSheet, useWindowDimensions, View } from 'react-native';
import { ProgressBar } from 'react-native-paper';

import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { useColor } from '@/hooks/useColor';
import { firebaseImage } from '@/utils/firebaseImage';
import { i18n } from '@/utils/i18n';

import BrandedButton from '../buttons/BrandedButton';
import CloseButton from '../buttons/CloseButton';
import DeleteButton from '../buttons/DeleteButton';
import EditButton from '../buttons/EditButton';
import Text from '../Text';

const DraggableImage = ({
  index,
  image,
  isLast,
  onEdit,
  onDelete
}: {
  index: number;
  image: string;
  isLast?: boolean;
  onEdit?: (image: string) => void;
  onDelete: (image: string) => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: image });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  const handleEdit = () => {
    onEdit && onEdit(image);
  };

  const handleRemove = () => {
    onDelete(image);
  };

  return Platform.OS === 'web' ? (
    <div style={style} ref={setNodeRef}>
      <div {...attributes} {...listeners}>
        <Image
          source={{ uri: image }}
          style={[
            styles.image,
            index === 0 && { borderTopLeftRadius: 16, borderBottomLeftRadius: 16 },
            isLast && { borderTopRightRadius: 16, borderBottomRightRadius: 16 }
          ]}
        />
      </div>
      {index === 0 && (
        <View style={styles.mainLabel}>
          <Text>{i18n.t('mix.main')}</Text>
        </View>
      )}
      {onEdit && (
        <View style={styles.editIcon}>
          <EditButton onPress={handleEdit} />
        </View>
      )}
      <View style={styles.deleteIcon}>
        <DeleteButton onPress={handleRemove} />
      </View>
    </div>
  ) : (
    <View>
      <Image
        source={{ uri: image }}
        style={styles.image}
        onLoad={() => {
          URL.revokeObjectURL(image);
        }}
      />
      <View style={styles.deleteIcon}>
        <DeleteButton onPress={handleRemove} />
      </View>
    </View>
  );
};

export type ImageDropZonePickerFieldProps<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  numberOfImages?: number;
  showError?: boolean;
  progress?: number;
  label?: string;
};

const ImageDropZonePickerField = <T extends FieldValues>({
  control,
  name,
  numberOfImages = 5,
  showError,
  progress,
  label
}: ImageDropZonePickerFieldProps<T>) => {
  const [imagesFiles, setImagesFiles] = useState<string[]>([]);
  const [showCropper, setShowCropper] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [currentImage, setCurrentImage] = useState<string | null>(null);
  const [indexToPick, setIndexToPick] = useState<number | null>(null);
  const [cropping, setCropping] = useState(false);

  const cropperRef = useRef<Cropper>(null);

  const { width, height } = useWindowDimensions();

  const imgSize = (width > height ? height : width) - 100;

  const [numOfColumns, setNumOfColumns] = useState(3);

  useEffect(() => {
    if (width > 1200 && numOfColumns !== 3) {
      setNumOfColumns(3);
    } else if (width > 820 && width < 1200 && numOfColumns !== 2) {
      setNumOfColumns(2);
    } else if (width < 820 && numOfColumns !== 1) {
      setNumOfColumns(1);
    }
  }, [numOfColumns, width]);

  const {
    field: { value, onChange },
    fieldState: { error }
  } = useController<T>({
    name,
    control
  });

  const { showError: errorModal } = useEndFlowModal();
  const colors = useColor();

  useEffect(() => {
    if (Array.isArray(value)) {
      const mappedValue: string[] = value.map((photo: string | undefined) => {
        return photo ? firebaseImage(photo) : undefined;
      });
      setImagesFiles(mappedValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCropFinished = async () => {
    if (!currentImage) {
      console.error('No selected image');
      return;
    }
    setCropping(true);

    const cropData = cropperRef.current?.getCropData();
    const areaPixels = cropData?.croppedAreaPixels;
    const width = areaPixels?.width;
    const height = areaPixels?.height;
    const x = areaPixels?.x;
    const y = areaPixels?.y;

    if (!width || width < 390 || !height || height < 390) {
      errorModal({
        title: i18n.t('mix.err_crop_further_title'),
        message: i18n.t('mix.err_crop_further_msg')
      });
      setCropping(false);
      return;
    }

    const newFilesArray = [...imagesFiles];
    await ImageManipulator.manipulateAsync(currentImage, [
      { crop: { width: width ?? 1000, height: height ?? 1000, originX: x ?? 0, originY: y ?? 0 } }
    ]).then((manipulatedImage) => {
      newFilesArray[indexToPick!] = manipulatedImage.uri;
    });

    try {
      const response = await fetch(newFilesArray[indexToPick!]!);
      const blob = await response.blob();

      const fileSize = blob.size / 1024 / 1024;

      if (fileSize >= 0.5 && fileSize <= 30) {
        setImagesFiles(newFilesArray);
      } else
        errorModal({
          title: i18n.t('mix.err_size_img'),
          message: i18n.t('mix.err_size_img_cropped', { FILE_SIZE: fileSize.toFixed(2) })
        });
    } catch (error) {
      errorModal({
        title: i18n.t('mix.err_getting_file_size')
      });
      console.error('Error getting file size:', error);
      return;
    }

    setIndexToPick(null);
    setCurrentImage(null);
    setShowCropper(false);
    setCropping(false);
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      let hasSizeError = false;
      const newImages = acceptedFiles.filter((file) => {
        const fileSize = file.size / 1024 / 1024;
        if (fileSize >= 0.5 && fileSize <= 30) {
          return file;
        } else {
          hasSizeError = true;
        }
      });
      if (hasSizeError)
        errorModal({
          title: i18n.t('mix.err_size_img'),
          message: i18n.t('mix.err_size_img_msg')
        });
      const newImagesPaths = newImages.map((image) => URL.createObjectURL(image));
      setImagesFiles((prev) => [...prev, ...newImagesPaths].slice(0, 5));
    },
    [errorModal]
  );

  const removeImage = (imageToRemove: string) => {
    setImagesFiles((prev) => prev.filter((image) => image !== imageToRemove));
  };

  const onDragEnd = (event: DragEndEvent) => {
    'worklet';
    const { active, over } = event;
    if (!over) return;
    if (active.id !== over.id) {
      const oldIndex = imagesFiles.indexOf(active.id.toString());
      const newIndex = imagesFiles.indexOf(over.id.toString());
      setImagesFiles((prev) => arrayMove(prev, oldIndex, newIndex));
    }
  };

  const { open, getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    multiple: true
  });

  const sensors = useSensors(useSensor(PointerSensor));

  useEffect(() => {
    onChange(imagesFiles);
  }, [imagesFiles, numberOfImages, onChange]);

  return (
    <View style={styles.container}>
      {label && (
        <Text fontWeight="700" style={styles.label}>
          {label}
        </Text>
      )}
      {imagesFiles.length === 0 ? (
        <button {...getRootProps()} style={styles.dropZone}>
          <input {...getInputProps()} />
          <Text fontWeight="700" style={styles.dropZoneText}>
            {i18n.t('mix.drag_drop_image_text')}
          </Text>
        </button>
      ) : (
        <>
          <button {...getRootProps()} style={styles.activeDropZone}>
            <input {...getInputProps()} />
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={imagesFiles} strategy={rectSortingStrategy}>
                <FlatList
                  //Needed to force refresh of columns on the FlatList
                  key={numOfColumns}
                  {...(numOfColumns > 1 && {
                    columnWrapperStyle: styles.flatListColumnWrapperStyle
                  })}
                  style={styles.flatList}
                  contentContainerStyle={styles.flatListContentContainerStyle}
                  data={imagesFiles}
                  numColumns={numOfColumns}
                  scrollEnabled={false}
                  keyExtractor={(item, index) => item ?? index.toString()}
                  renderItem={({ item, index }) =>
                    item !== undefined ? (
                      <View style={{ flex: 1 / numOfColumns }}>
                        <DraggableImage
                          key={item}
                          index={index}
                          isLast={index === imagesFiles.length - 1}
                          image={item}
                          {...(!item.includes('firebasestorage') && {
                            onEdit: (image) => {
                              setIndexToPick(index);
                              setCurrentImage(image);
                              setShowCropper(true);
                            }
                          })}
                          onDelete={removeImage}
                        />
                      </View>
                    ) : null
                  }
                />
              </SortableContext>
            </DndContext>
          </button>
          <BrandedButton style={styles.addMoreButton} onPress={open}>
            {i18n.t('mix.add_more_images')}
          </BrandedButton>
        </>
      )}
      {/* Cropper Modal */}
      <Modal visible={showCropper} transparent animationType="fade" onRequestClose={() => setShowCropper(false)}>
        <View style={styles.overlay}>
          <Pressable style={StyleSheet.absoluteFill} onPress={() => setShowCropper(false)} />
          <View
            style={[
              {
                height: imgSize,
                width: imgSize
              },
              styles.cropImgContainer
            ]}>
            <View style={styles.cropImgHeader}>
              <Text style={[styles.selectTypeHeader, { color: colors.titleText }]} fontWeight="900">
                {i18n.t('mix.crop_img_modal_title')}
              </Text>
              <CloseButton
                onCloseTap={() => {
                  setShowCropper(false);
                }}
              />
            </View>
            <View style={styles.cropperContainer}>
              {cropping && <View style={styles.croppingForegroundView} />}
              <Cropper
                ref={(node) => {
                  if (node) cropperRef.current = node;
                }}
                image={currentImage ?? ''}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
              />
            </View>
            <BrandedButton onPress={onCropFinished} style={styles.cropBtn} loading={cropping}>
              {i18n.t('mix.done')}
            </BrandedButton>
          </View>
        </View>
      </Modal>

      {progress !== undefined && progress >= 0 && progress <= 100 && (
        <View style={styles.uploadingContainer}>
          <Text style={styles.uploadingLabel}>{i18n.t('mix.uploading')}</Text>
          <ProgressBar style={styles.progressBar} animatedValue={progress / 100} color="gold" />
        </View>
      )}

      {showError && !!error?.message && <Text style={styles.errorText}>{error.message}</Text>}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center'
  },

  dropZone: {
    width: '100%',
    height: 390,
    borderWidth: 2,
    borderColor: '#aaa',
    borderStyle: 'dashed',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 16,
    backgroundColor: '#D9D9D9',
    padding: 16
  },

  activeDropZone: {
    width: '100%',
    height: '100%',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#D9D9D9',
    borderRadius: 16
  },

  dropZoneText: {
    flex: 1,
    color: 'white',
    fontSize: 20
  },

  addMoreButton: {
    width: '100%',
    marginTop: 10
  },

  flatList: {
    width: '100%'
  },

  flatListContentContainerStyle: {
    flex: 1,
    gap: 2
  },

  flatListColumnWrapperStyle: {
    flex: 1,
    gap: 2
  },

  image: {
    height: 390,
    width: '100%'
  },

  mainLabel: {
    position: 'absolute',
    bottom: 10,
    left: 5,
    backgroundColor: 'white',
    paddingHorizontal: 10,
    paddingVertical: 1,
    borderRadius: 10,
    zIndex: 10
  },

  editIcon: {
    position: 'absolute',
    top: 10,
    right: 42,
    backgroundColor: 'white',
    padding: 5,
    borderRadius: 99,
    zIndex: 10
  },

  deleteIcon: {
    position: 'absolute',
    top: 10,
    right: 5,
    backgroundColor: 'white',
    padding: 5,
    borderRadius: 99,
    zIndex: 10
  },

  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    justifyContent: 'center',
    alignItems: 'center'
  },

  uploadingContainer: {
    flexDirection: 'column',
    height: 45,
    width: '100%'
  },

  uploadingLabel: {
    width: '100%',
    color: 'black',
    marginTop: 10
  },

  progressBar: {
    marginTop: 10,
    backgroundColor: 'gray'
  },

  selectTypeHeader: {
    fontSize: 22,
    color: 'black',
    justifyContent: 'center',
    alignSelf: 'center',
    textAlign: 'center'
  },

  errorText: {
    marginTop: 10,
    fontSize: 12,
    color: 'red'
  },

  label: {
    color: 'black',
    marginBottom: 10
  },

  cropImgContainer: {
    backgroundColor: 'white',
    borderRadius: 10,
    padding: 20,
    flexDirection: 'column',
    rowGap: 20
  },

  cropImgHeader: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },

  cropperContainer: {
    flex: 1,
    borderRadius: 10,
    overflow: 'hidden'
  },

  cropBtn: {
    paddingHorizontal: 10
  },

  croppingForegroundView: {
    backgroundColor: 'rgba(255,255,255,0.5)',
    flex: 1
  }
});

export default ImageDropZonePickerField;
